<template>
  <div class="flex flex-row shadow-md gap-2 rounded-xl mb-4 bg-white p-1">
    <button class="subtab" @click="go(RouteName.Events)">
      {{ t("events.list.list") }}
    </button>
    <button class="subtab" @click="go(RouteName.EventDetail, { id: id })">
      {{ t("events.detail.detail") }}
    </button>
    <button class="subtab active">
      {{ t("events.edit.edit") }}
    </button>
  </div>
  <div v-if="isLoading" class="flex h-[60vh] justify-center">
    <LoadingIcon :isLoading="isLoading" />
  </div>
  <div class="flex flex-col gap-4" v-if="!isLoading && eventDetail">
    <div class="flex flex-row justify-between items-center">
      <div class="flex flex-col gap-2">
        <h1 class="font-extrabold text-2xl">{{ t("events.detail.title") }}</h1>
        <div class="text-light-gray text-sm">
          {{ eventDetail.name }}
        </div>
      </div>
      <div class="flex flex-row gap-2 h-[50%]">
        <CustomButton
          @click="go(RouteName.EventDetail, { id: id })"
          :title="t('common.cancel')"
          button-styles="bg-white! text-gray-600!"
        />

        <CustomButton
          @click="editEvent"
          :title="t('common.save')"
          :disabled="isSubmitting || !eventDetailFormRef?.meta.dirty"
        >
          <IconManager name="Save" />
        </CustomButton>
      </div>
    </div>
    <div class="grid grid-cols-2 gap-3 my-4">
      <InfoBox
        :title="t('events.detail.status')"
        :value="eventDetail.status.getLocalization()"
        :backgroundColor="EventState.events[0].lightColor"
        :textColor="EventState.events[0].color"
      >
        <IconManager
          name="Calendar"
          :style="{ color: EventState.events[0].color }"
        />
      </InfoBox>

      <InfoBox
        :title="t('events.detail.location')"
        :value="eventDetail.location"
        :backgroundColor="EventState.events[1].lightColor"
        :textColor="EventState.events[1].color"
      >
        <IconManager
          name="Search"
          :style="{ color: EventState.events[1].color }"
        />
      </InfoBox>
    </div>
    <div class="flex flex-row gap-4">
      <VeeForm
        ref="eventDetailFormRef"
        class="flex flex-col gap-3 bg-white rounded-xl shadow-md p-4 w-full"
        v-slot="{ meta, values, validate }"
        :initial-values="{
          name: eventDetail.name,
          startDate: eventDetail.startDate,
          endDate: eventDetail.endDate,
          numberOfParticipants: eventDetail.numberOfParticipants,
          budget: eventDetail.budget,
          expense: eventDetail.expense,
          requestingService: eventDetail.requestingService,
          team: eventDetail.team,
          location: eventDetail.location,
          venueType: eventDetail.venueType,
          description: eventDetail.description,
        }"
      >
        <label class="font-extrabold">
          {{ t("events.detail.generalInformation") }}
        </label>
        <CustomInput
          :title="t('common.name')"
          name="name"
          rules="required"
          readonly
        />

        <CustomDatePicker
          :title="t('events.detail.creationDate')"
          :modelValue="eventDetail.creationDate"
          :clearable="true"
          :placeholder="t('events.detail.creationDate')"
          :readonly="true"
          class="w-fit"
        />

        <CustomDatePicker
          :title="t('events.detail.startDate')"
          :clearable="true"
          :placeholder="t('events.detail.startDate')"
          name="startDate"
          rules="required"
          required
          class="w-fit"
        />

        <CustomDatePicker
          :title="t('events.detail.endDate')"
          name="endDate"
          :clearable="true"
          :placeholder="t('events.detail.endDate')"
          rules="required"
          required
          class="w-fit"
        />

        <CustomInput
          :title="t('events.detail.numberOfParticipants')"
          name="numberOfParticipants"
          type="number"
          rules="required"
          readonly
        />

        <CustomInput
          :title="t('events.detail.budget')"
          name="budget"
          type="number"
          rules="required"
          readonly
        />

        <CustomInput
          :title="t('events.detail.expense')"
          name="expense"
          type="number"
          rules="required"
          readonly
        />

        <CustomInput
          :title="t('events.detail.requestingService')"
          name="requestingService"
          rules="required"
          readonly
        />

        <CustomInput
          :title="t('events.detail.team')"
          name="team"
          rules="required"
          readonly
        />

        <CustomInput
          :title="t('events.detail.location')"
          name="location"
          rules="required"
          readonly
        />

        <!-- TODO:Reviw this part, or remove or pass to dropdown -->
        <CustomInput
          :title="t('events.detail.venueType')"
          name="venueType"
          rules="required"
          readonly
        />

        <CustomTextArea
          :title="t('events.detail.description')"
          name="description"
          readonly
        />
      </VeeForm>
      <div class="flex flex-col gap-3 bg-white rounded-xl shadow-md p-4 w-full">
        <label class="font-extrabold">
          {{ t("events.detail.addResource") }}
        </label>
        <CustomDropDown
          :title="t('events.detail.associateTo')"
          :data="subEventsAssociatedTo"
          dataKey="id"
          dataValue="name"
          v-model="selectedAssociateTo"
        />

        <CustomDropDown
          :title="t('events.detail.resourceType')"
          :data="resources"
          dataKey="id"
          dataValue="name"
          subOptionsKey="subTypes"
          sub-option-key="id"
          sub-option-value="name"
          v-model="selectedResourceType"
        />

        <div class="flex flex-col gap-2" v-if="selectedResourceType">
          <div
            v-for="(range, index) in dateRanges"
            :key="index"
            class="flex flex-row gap-2 items-end"
          >
            <CustomDatePicker
              :title="t('events.detail.startDate')"
              v-model="range.startDate"
              :clearable="true"
              :placeholder="t('events.detail.startDate')"
            />

            <CustomDatePicker
              :title="t('events.detail.endDate')"
              v-model="range.endDate"
              :clearable="true"
              :placeholder="t('events.detail.endDate')"
            />

            <CustomButton
              v-if="index > 0"
              @click="removeDateRange(index)"
              buttonStyles="text-red-500! bg-red-200! rounded-full! p-2!"
            >
              <IconManager name="Delete" />
            </CustomButton>
          </div>

          <div>
            <CustomButton
              @click="addDateRange"
              :title="t('events.detail.addOtherInteval')"
              buttonStyles="rounded-full! p-2! text-primary bg-secondary!"
            >
              <IconManager name="Add" />
            </CustomButton>
          </div>
          <CustomButton
            @click="getAvailableResources"
            :title="t('common.search')"
            :is-loading="isSearching"
            :disabled="
              dateRanges.some(
                (date) =>
                  date.startDate.length == 0 || date.endDate.length == 0,
              )
            "
          />
        </div>

        <ResourceCardView
          v-for="item in availableResources"
          :data="item"
          :interval-date="dateRanges"
          :submit-call-back="submitAllocateResource"
        />
      </div>
    </div>
    <div class="flex flex-col gap-4 bg-white rounded-xl shadow-md p-4 w-full">
      <div class="flex flex-col gap-4">
        <span class="font-extrabold">
          {{ t("events.detail.allocatedResources") }}
        </span>
        <InfoCardView
          v-for="item in eventDetail.resourceTypes"
          :data="item"
          :actions="[
            {
              icon: 'Delete',
              onClick: removeResource,
              colorClass: 'text-red-500! bg-red-200! p-2!',
              isLoading: isSubmitting,
            },
          ]"
        />
      </div>
      <div class="flex flex-col gap-4 py-2 border-t-1 border-t-[#E5E9EF]">
        <span class="font-extrabold">
          {{ t("events.detail.subEventAllocatedResources") }}
        </span>
        <div v-for="subEvent in eventDetail.subEvents">
          <InfoCardView
            v-for="item in subEvent.resourceTypes"
            :data="item"
            :actions="[
              {
                icon: 'Eye',
                onClick: (r) => go(RouteName.ResourceDetail, { id: r.id }),
              },
            ]"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-3 bg-white rounded-xl shadow-md p-4 w-full">
      <div class="grid grid-cols-3 items-center">
        <span class="font-extrabold text-md">
          {{ t("events.detail.subEvents.title") }}
        </span>
        <span class="font-semibold text-light-gray text-xs justify-self-center">
          {{ t("events.detail.subEvents.subTitle") }}
        </span>
        <div class="justify-self-end">
          <CustomButton
            @click="
              isDialogOpen = true;
              isSubEventEditMode = false;
            "
            :disabled="isSubmitting"
            :title="t('events.detail.subEvents.newSubEvent')"
          >
            <IconManager name="Add" />
          </CustomButton>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <SubEventCardView
          v-for="item in eventDetail.subEvents"
          :data="item"
          :edit-call-back="
            () => {
              setSubEventValues({
                subEventName: item.name,
                startDate: item.startDate.toISOString(),
                endDate: item.endDate.toISOString(),
                localization: item.location,
                id: item.id,
              });
              isSubEventEditMode = true;
              isDialogOpen = true;
            }
          "
        />
        <CustomDialog
          v-model="isDialogOpen"
          :title="
            t(
              isSubEventEditMode
                ? 'events.detail.subEvents.editSubEvent'
                : 'events.detail.subEvents.newSubEvent',
            )
          "
          :is-submitting="isSubmitting"
          :disable-submit-button="!subEventMeta.valid"
          @submit="
            () =>
              isSubEventEditMode ? submitEditSubEvent() : submitCreateSubEvent()
          "
          @cancel="isDialogOpen = false"
          :submitText="
            t(
              isSubEventEditMode
                ? 'events.detail.subEvents.editSubEvent'
                : 'events.detail.subEvents.createSubEvent',
            )
          "
        >
          <div class="flex flex-col gap-3">
            <CustomInput
              :title="t('common.name')"
              readonly
              v-model="eventDetail.name"
            />
            <CustomInput
              :title="t('events.detail.subEvents.subEventName')"
              name="subEventName"
              rules="required"
              required
            />
            <div class="flex flex-row gap-2 justify-between">
              <CustomDatePicker
                :title="t('events.detail.startDate')"
                :placeholder="t('events.detail.startDate')"
                name="startDate"
                rules="required"
                required
              />
              <CustomDatePicker
                :title="t('events.detail.endDate')"
                :placeholder="t('events.detail.endDate')"
                name="endDate"
                rules="required"
                required
                :minDate="subEventValues.startDate"
                :readonly="subEventValues.startDate?.length == 0"
              />
            </div>
            <CustomInput
              :title="t('events.detail.location')"
              name="localization"
              rules="required"
              required
            />
          </div>
        </CustomDialog>
      </div>
    </div>
    <div
      class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full"
      v-if="timelineEvents.length > 0"
    >
      <div class="flex flex-row w-full justify-between">
        <span class="font-extrabold">
          {{ t("events.edit.timeline.title") }}
        </span>
      </div>
      <TimelineChart
        :events="timelineEvents"
        :callBack="(id: string) => go(RouteName.ResourceDetail, { id: id })"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import InfoBox from "@/core/components/InfoBox.vue";
import CustomDropDown from "@/core/components/CustomDropDown.vue";
import CustomDatePicker from "@/core/components/CustomDatePicker.vue";
import CustomInput from "@/core/components/CustomInput.vue";
import CustomTextArea from "@/core/components/CustomTextArea.vue";
import CustomButton from "@/core/components/CustomButton.vue";
import "./EventDetailPage.scss";
import InfoCardView from "@/core/components/InfoCardView.vue";
import SubEventCardView from "@/modules/events/views/SubEventCardView.vue";
import ResourceCardView from "@/modules/events/views/ResourceCardView.vue";
import { useI18n } from "vue-i18n";
import {
  eventsRemoteDataSource,
  resourceRemoteDataSource,
} from "@/core/models/global-variables";
import { EventState } from "../models/event-state";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { EventDetail } from "../models/event-detail";
import { RouteName } from "@/core/models/route-name";
import { ResourceType } from "@/modules/resources/models/resource-type";
import { ResourceSubType } from "@/modules/resources/models/resource-sub-type";
import { ResourceResumed } from "@/modules/resources/models/resource-resumed";
import { ResourceDetail } from "@/modules/resources/models/resource-detail";
import CustomDialog from "@/core/components/CustomDialog.vue";
import { useForm } from "vee-validate";
import { SubEvent } from "../models/sub-event";
import { Form as VeeForm } from "vee-validate";
import { ResourcePermanency } from "@/modules/resources/models/resource-permanecy";
import TimelineChart from "@/core/components/charts/TimelineChart.vue";
import LoadingIcon from "@/core/components/LoadingIcon.vue";
import { Utils } from "@/core/models/utils";

export interface DateRange {
  startDate: string;
  endDate: string;
}

const props = defineProps<{
  id: string;
}>();

const { t, locale } = useI18n();
const { showError, showSuccess } = useToast();

const router = useRouter();
const route = useRoute();
const eventDetail = ref<EventDetail | null>(null);
const isLoading = ref<boolean>(true);
const resources = ref<ResourceType[]>([]);
const selectedAssociateTo = ref<SubEvent | null>(null);
const selectedResourceType = ref<{
  option: ResourceType;
  subOption: ResourceSubType;
} | null>(null);
const availableResources = ref<ResourceResumed[]>([]);
const subEventsAssociatedTo = ref<SubEvent[]>([]);
const dateRanges = ref<DateRange[]>([
  { startDate: new Date().toISOString(), endDate: "" },
]);

const isSearching = ref<boolean>(false);

const eventDetailFormRef = ref<InstanceType<typeof VeeForm>>();

const isSubEventEditMode = ref<boolean>(false);

const {
  values: subEventValues,
  meta: subEventMeta,
  validate: validateSubEvent,
  setValues: setSubEventValues,
} = useForm({
  initialValues: {
    subEventName: "",
    startDate: "",
    endDate: "",
    localization: "",
    id: -1,
  },
});

const timelineEvents = computed(() =>
  eventDetail.value!.occupancyCalendarResources.map((item) => ({
    id: item.resourceId,
    title: item.name,
    events: item.intervals.flatMap((interval) =>
      interval.bars.map((bar) => ({
        id: interval.eventId,
        startDate: bar.startDate,
        endDate: bar.endDate,
        name: interval.name,
        color: Utils.getRandomColor(),
        tooltipContent: buildTooltipHtml(
          interval.eventId,
          interval.quantity,
          new Date(bar.startDate),
          new Date(bar.endDate),
        ),
      })),
    ),
  })),
);

//Dialog
const isDialogOpen = ref(false);
const isSubmitting = ref(false);

onMounted(() => {
  loadInitialData();
  validateSubEvent();
});

function addDateRange() {
  dateRanges.value.push({ startDate: "", endDate: "" });
}

function removeDateRange(index: number) {
  dateRanges.value.splice(index, 1);
}

async function submitAllocateResource(
  resourceId: string,
  quantity: number,
  resourcePermanency: ResourcePermanency,
) {
  isSubmitting.value = true;
  try {
    const response = await resourceRemoteDataSource.allocateResource(
      {
        quantity: quantity,
        resourceId: resourceId,
        intervals: dateRanges.value,
        subEventId:
          selectedAssociateTo.value!.id.toString() == eventDetail.value!.id
            ? undefined
            : selectedAssociateTo.value!.id,
        permanency: resourcePermanency,
      },
      eventDetail.value!.id,
    );
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    } else showSuccess({ title: t("message.addedSucess") });
    await getAvailableResources();

    const getAllocatedResources = await eventsRemoteDataSource.getEvent(
      props.id,
    );

    if (!getAllocatedResources.isSuccess) {
      showError({
        message: getAllocatedResources.detail!,
        title: getAllocatedResources.title,
        statusCode: getAllocatedResources.statusCode,
      });
    }
    eventDetail.value!.resourceTypes = getAllocatedResources.data.resourceTypes;
    eventDetail.value!.subEvents = getAllocatedResources.data.subEvents;
    eventDetail.value!.occupancyCalendarResources =
      getAllocatedResources.data.occupancyCalendarResources;
    isDialogOpen.value = false;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isSubmitting.value = false;
  }
}

async function submitCreateSubEvent() {
  isSubmitting.value = true;
  try {
    const createEventResponse = await eventsRemoteDataSource.createSubEvent(
      eventDetail.value!.id,
      {
        name: subEventValues.subEventName,
        location: subEventValues.localization,
        startDate: subEventValues.startDate,
        endDate: subEventValues.endDate,
      },
    );
    if (!createEventResponse.isSuccess) {
      showError({
        message: createEventResponse.detail!,
        title: createEventResponse.title,
      });
    } else showSuccess({ title: t("message.createdSucess") });

    const getEventsResponse = await eventsRemoteDataSource.getSubEvents(
      eventDetail.value!.id,
    );
    if (!getEventsResponse.isSuccess) {
      showError({
        message: getEventsResponse.detail!,
        title: getEventsResponse.title,
        statusCode: getEventsResponse.statusCode,
      });
    }
    eventDetail.value!.subEvents = getEventsResponse.data;
    isDialogOpen.value = false;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isSubmitting.value = false;
  }
}

async function submitEditSubEvent() {
  isSubmitting.value = true;
  try {
    const createEventResponse = await eventsRemoteDataSource.editSubEvent(
      eventDetail.value!.id,
      subEventValues.id,
      {
        name: subEventValues.subEventName,
        location: subEventValues.localization,
        startDate: subEventValues.startDate,
        endDate: subEventValues.endDate,
      },
    );
    if (!createEventResponse.isSuccess) {
      showError({
        message: createEventResponse.detail!,
        title: createEventResponse.title,
        statusCode: createEventResponse.statusCode,
      });
    } else showSuccess({ title: t(".messageeditedSucess") });

    const getEventsResponse = await eventsRemoteDataSource.getSubEvents(
      eventDetail.value!.id,
    );
    if (!getEventsResponse.isSuccess) {
      showError({
        message: getEventsResponse.detail!,
        title: getEventsResponse.title,
        statusCode: getEventsResponse.statusCode,
      });
    }
    eventDetail.value!.subEvents = getEventsResponse.data;
    isDialogOpen.value = false;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isSubmitting.value = false;
  }
}

async function getAvailableResources() {
  isSearching.value = true;

  try {
    const response = await resourceRemoteDataSource.getAvailableResources({
      type: selectedResourceType.value!.option.name,
      subType: selectedResourceType.value!.subOption.name,
      intervals: dateRanges.value,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    availableResources.value = response.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isSearching.value = false;
  }
}

async function editEvent() {
  isLoading.value = true;

  try {
    if (!eventDetailFormRef.value) return;
    const editEventResponse = await eventsRemoteDataSource.editEvent(
      eventDetail.value!.id,
      {
        name: eventDetailFormRef.value.values.name,
        startDate: eventDetailFormRef.value.values.startDate,
        endDate: eventDetailFormRef.value.values.endDate,
        team: eventDetailFormRef.value.values.team,
        numberOfParticipants:
          eventDetailFormRef.value.values.numberOfParticipants,
        requestingService: eventDetailFormRef.value.values.requestingService,
        venueType: eventDetailFormRef.value.values.venueType,
        type: eventDetailFormRef.value.values.type,
        location: eventDetailFormRef.value.values.location,
        description: eventDetailFormRef.value.values.description,
      },
    );
    if (!editEventResponse.isSuccess) {
      showError({
        message: editEventResponse.detail!,
        title: editEventResponse.title,
        statusCode: editEventResponse.statusCode,
      });
    }

    const getEventResponse = await eventsRemoteDataSource.getEvent(
      eventDetail.value!.id,
    );
    if (!getEventResponse.isSuccess) {
      showError({
        message: getEventResponse.detail!,
        title: getEventResponse.title,
        statusCode: getEventResponse.statusCode,
      });
    }
    eventDetail.value = getEventResponse.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

async function removeResource(resourceDetail: ResourceDetail) {
  isSubmitting.value = true;

  try {
    const response = await eventsRemoteDataSource.removeResourceFromEvent(
      eventDetail.value!.id,
      Number(resourceDetail.id),
    );
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    const getAllocatedResources = await eventsRemoteDataSource.getEvent(
      props.id,
    );

    if (!getAllocatedResources.isSuccess) {
      showError({
        message: getAllocatedResources.detail!,
        title: getAllocatedResources.title,
        statusCode: getAllocatedResources.statusCode,
      });
    } else showSuccess({ title: t("message.deletedSucess") });
    eventDetail.value!.resourceTypes = getAllocatedResources.data.resourceTypes;
    eventDetail.value!.subEvents = getAllocatedResources.data.subEvents;
    eventDetail.value!.occupancyCalendarResources =
      getAllocatedResources.data.occupancyCalendarResources;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isSubmitting.value = false;
  }
}

async function loadInitialData() {
  isLoading.value = true;

  try {
    const [eventsRes, resourcesRes] = await Promise.all([
      eventsRemoteDataSource.getEvent(route.params.id as string),
      resourceRemoteDataSource.getResourcesStructure(),
    ]);
    if (!eventsRes.isSuccess) {
      showError({
        message: eventsRes.detail!,
        title: eventsRes.title,
        statusCode: eventsRes.statusCode,
      });
    }
    eventDetail.value = eventsRes.data;
    if (
      eventDetail.value!.status.value == EventState.events[4].value ||
      eventDetail.value!.status.value == EventState.events[5].value
    ) {
      router.replace({ name: RouteName.EventDetail, params: { id: props.id } });
    }
    if (!resourcesRes.isSuccess) {
      showError({
        message: resourcesRes.detail!,
        title: resourcesRes.title,
        statusCode: resourcesRes.statusCode,
      });
      return;
    }
    resources.value = resourcesRes.data;

    subEventsAssociatedTo.value.push({
      id: Number(eventDetail.value!.id),
      startDate: eventDetail.value!.startDate,
      endDate: eventDetail.value!.endDate,
      location: eventDetail.value!.location!,
      name: t("events.detail.mainEvent") + " - " + eventDetail.value!.id,
    });
    subEventsAssociatedTo.value.push(
      ...eventDetail.value!.subEvents!.map((subEvent) => {
        subEvent.name = subEvent.name + " - " + subEvent.id;
        return subEvent;
      }),
    );
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

function go(name: string, params: Record<string, any> = {}) {
  router.push({
    name,
    params,
  });
}

function buildTooltipHtml(
  eventId: string,
  quantity: number,
  startDate: Date,
  endDate: Date,
): string {
  return `
    <div style="font-size: 12px; line-height: 1.6;">
      <div><strong>${t("events.detail.timeline.tooltip.name")}:</strong> ${eventId}</div>
       <div><strong>${t("events.detail.timeline.tooltip.duration")}:</strong> ${Utils.formatDate(startDate, locale.value)} → ${Utils.formatDate(endDate, locale.value)}</div>
      <div><strong>${t("events.detail.timeline.tooltip.quantity")}:</strong> ${quantity}</div>
    </div>
  `;
}
</script>
